import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '#why-verify', label: 'Why Verify' },
    { href: '#certification', label: 'Certifications' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Geneva Financial District Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=panoramic%20wide%20angle%20aerial%20photography%20Geneva%20Switzerland%20financial%20district%20Rue%20du%20Nant%20luxury%20corporate%20headquarters%20elegant%20stone%20buildings%20golden%20hour%20dusk%20warm%20amber%20light%20sophisticated%20European%20banking%20capital%20clear%20sky%20glass%20facade%20modern%20architecture%20premium%20corporate%20real%20estate%20private%20banking%20headquarters%20prestigious%20street%20level%20view%20high%20resolution&width=1920&height=1080&seq=bpi-geneva-hero-v9&orientation=landscape"
          alt="BPI Holding SA – Geneva Financial District"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer overlay for premium readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#001529]/85 via-[#001529]/65 to-[#001529]/92"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529]/50 via-transparent to-[#001529]/50"></div>
      </div>

      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      ></div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#001529]/98 backdrop-blur-2xl border-b border-slate-700/50'
            : ''
        }`}
      >
        <div className="px-5 sm:px-10 lg:px-16 py-4 sm:py-5 flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 cursor-pointer">
            <div className="w-9 h-9 bg-emerald-500 rounded-md flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-fill text-white text-base"></i>
            </div>
            <div>
              <div
                className="text-white font-black text-xs sm:text-sm leading-tight tracking-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                VERIFICATION CRYPTO
              </div>
              <div className="text-emerald-400 text-xs font-black tracking-[0.12em] uppercase leading-tight">
                AML/KYC
              </div>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-300 hover:text-white transition-colors text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://mycryptoaml.com/verification"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2.5 rounded-md text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shield-check-line text-sm"></i>
              Verify Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Dropdown */}
        {menuOpen && (
          <div className="md:hidden bg-[#001529]/99 backdrop-blur-xl border-t border-slate-700/50">
            <div className="px-5 py-5 space-y-1">
              {navLinks.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="block text-slate-200 hover:text-emerald-400 transition-colors text-base font-medium py-3 border-b border-slate-700/30 cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
              <a
                href="https://mycryptoaml.com/verification"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4 bg-emerald-500 hover:bg-emerald-400 text-white text-center py-4 rounded-lg font-bold cursor-pointer transition-all"
              >
                <i className="ri-shield-check-line mr-2"></i>
                Proceed to Asset Verification
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-10 text-center pt-32 pb-20 md:pt-0 md:pb-0 w-full">

        {/* Swiss Authority Badge */}
        <div className="inline-flex items-center gap-2.5 border border-slate-500/40 bg-white/5 backdrop-blur-sm rounded-full px-5 py-2 mb-9">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
          </div>
          <span className="text-slate-300 text-xs font-semibold tracking-widest uppercase whitespace-nowrap">
            Swiss Institutional AML Authority
          </span>
        </div>

        {/* Main Headline */}
        <h1
          className="text-[2.6rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] font-black mb-5 leading-[1.06] tracking-tight"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          <span className="text-white block">BPI Institutional</span>
          <span className="text-white block">Digital Asset</span>
          <span
            className="block"
            style={{
              background: 'linear-gradient(90deg, #34d399, #6ee7b7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Verification
          </span>
        </h1>

        {/* Sub-headline */}
        <p
          className="text-slate-300 text-lg sm:text-xl md:text-2xl mb-4 max-w-3xl mx-auto leading-relaxed font-light tracking-wide px-2"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Swiss-certified smart contract auditing for{' '}
          <strong className="text-white font-semibold">USDC/USDT (ERC-20)</strong>.
        </p>

        {/* Trust line */}
        <p className="text-slate-500 text-sm mb-10 tracking-wide">
          Aligned with Swiss FINMA · FATF · OFAC · EU MiCA Standards
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-16">
          <a
            href="https://mycryptoaml.com/verification"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-white px-9 py-4 sm:py-5 rounded-lg font-black text-base sm:text-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <i className="ri-shield-check-line text-xl"></i>
            Proceed to Asset Verification
          </a>
          <a
            href="#why-verify"
            className="w-full sm:w-auto text-center border border-slate-500/50 bg-white/5 text-slate-200 px-9 py-4 sm:py-5 rounded-lg font-semibold text-base sm:text-lg hover:bg-white/10 hover:border-slate-400/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Why Verify?
          </a>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-3 gap-3 sm:gap-5 max-w-xl mx-auto">
          {[
            { value: '$1.2B+', label: 'Assets Verified' },
            { value: '4,500+', label: 'Contracts Flagged' },
            { value: '120+', label: 'Inst. Partners' },
          ].map((stat, i) => (
            <div
              key={i}
              className="border border-white/10 bg-white/5 backdrop-blur-sm rounded-lg p-3 sm:p-5"
            >
              <div
                className="text-xl sm:text-3xl font-black text-emerald-400 leading-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {stat.value}
              </div>
              <div className="text-slate-400 text-xs mt-0.5 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 animate-bounce">
        <span className="text-slate-600 text-[10px] font-bold tracking-[0.3em] uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-xl text-emerald-500/60"></i>
      </div>
    </section>
  );
}
